import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('No reset token provided. Please request a new password reset email.');
    }
  }, [token]);

  const passwordTooShort = password.length > 0 && password.length < 8;
  const passwordsMismatch = password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword;
  const canSubmit = !!token && password.length >= 8 && password === confirmPassword && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password,
      });

      // Do NOT auto-login — send the user back to Sign In to log in with the
      // new password (clearer + safer than silently authenticating them).
      setSuccess(true);
      toast.success('Password reset successful. Please sign in with your new password.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to reset password. Please request a new reset link.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-10 w-10" />
            <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              BillPortal
            </h1>
          </div>
          <p className="text-muted-foreground text-sm uppercase tracking-wide font-medium">
            Take control over your Utilities
          </p>
        </div>

        <div className="bg-card border border-border rounded-none p-8">
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
            data-testid="reset-back-to-login"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-primary/10 flex items-center justify-center">
              <KeyRound className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Choose a new password</h2>
              <p className="text-sm text-muted-foreground">Minimum 8 characters</p>
            </div>
          </div>

          {success ? (
            <div className="text-center py-4" data-testid="reset-success-panel">
              <div className="mx-auto h-12 w-12 bg-emerald-100 flex items-center justify-center mb-4">
                <KeyRound className="h-6 w-6 text-emerald-700" />
              </div>
              <h3 className="text-lg font-bold tracking-tight mb-1">Password reset successful</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Your password has been updated. Please sign in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full rounded-none font-bold uppercase tracking-wide"
                data-testid="reset-success-signin-button"
              >
                Sign In
              </Button>
            </div>
          ) : (
          <>
          {!token && (
            <div className="bg-amber-50 border border-amber-300 p-4 mb-6 flex items-start gap-3" data-testid="reset-missing-token-banner">
              <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
              <div className="text-sm text-amber-900">
                This page requires a reset token. Please use the link from the email we sent you, or{' '}
                <Link to="/forgot-password" className="underline font-medium">request a new reset email</Link>.
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <Label htmlFor="new-password" className="text-xs uppercase tracking-wide font-medium">
                New password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="new-password"
                  data-testid="new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="rounded-none pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!token}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordTooShort && (
                <p className="text-xs text-destructive mt-1" data-testid="password-too-short-error">
                  Password must be at least 8 characters.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="confirm-password" className="text-xs uppercase tracking-wide font-medium">
                Confirm new password
              </Label>
              <Input
                id="confirm-password"
                data-testid="confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                required
                className="rounded-none mt-1"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={!token}
              />
              {passwordsMismatch && (
                <p className="text-xs text-destructive mt-1" data-testid="passwords-mismatch-error">
                  Passwords do not match.
                </p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="reset-password-button"
              className="w-full rounded-none font-bold uppercase tracking-wide"
              disabled={!canSubmit}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
